import { Observable } from 'rxjs';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';

import { Injectable, Signal, computed } from '@angular/core';

import { EntityDocument } from '@mitsubishi/documents/data-access';
import {
  CurrencyUtils,
  CustomAccessory,
  CustomUnit,
  PerformanceAccessoryAnswer,
  PriceTypeEnum,
  SelectionRequestLogScope,
  Unit,
  UnitAlertSeenSeverity,
} from '@mitsubishi/shared/data-access';
import { SpinnerService, StateSpinnerService } from '@mitsubishi/ui/spinner';
import { getStateActionErroredFactory, getStateActionResultFactory } from '@mitsubishi/utils';

import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Actions, Store, ofActionSuccessful } from '@ngxs/store';

import {
  AdditionalInfo,
  Alternative,
  MoveChildRequest,
  Selection,
  SelectionDetails,
  StatisticDataArray,
} from '../../models';
import { UpdateOrderFieldsRequest } from '../../models/requests/update-order-fields.model';
import { ProjectActions } from '../project/project.actions';
import { ProjectSelectors } from '../project/project.selectors';
import { SelectionActions } from './selections.actions';
import { ProjectSelectionsSelectors } from './selections.selectors';

@Injectable({
  providedIn: 'root',
})
export class ProjectSelectionsFacade extends StateSpinnerService {
  private readonly onActionErrored = getStateActionErroredFactory();
  private readonly onActionResult = getStateActionResultFactory();

  selectionsSignal = this.store.selectSignal(ProjectSelectors.projectSelections);
  activeSelection$ = this.store
    .select(ProjectSelectionsSelectors.getActiveSelection)
    .pipe(distinctUntilChanged(), shareReplay({ refCount: true, bufferSize: 1 }));
  activeSelectionSignal = this.store.selectSignal(ProjectSelectionsSelectors.getActiveSelection);
  activeSelectionUnitsSignal = this.store.selectSignal(ProjectSelectionsSelectors.getActiveSelectionUnits);
  activeSelectionOrganizationId = this.store.selectSignal(ProjectSelectionsSelectors.getActiveSelectionOrganizationId);
  areDatasheetsGenerating = this.store.selectSignal(ProjectSelectionsSelectors.areDatasheetsGenerating);
  isCustomCurrencyApplied = this.store.selectSignal(ProjectSelectionsSelectors.isCustomCurrencyApplied);

  activeSelectionOwnerId$ = this.store.select(ProjectSelectionsSelectors.getActiveQuoteOwnerId);

  activeSelectionDocuments$: Observable<EntityDocument[]> = this.store.select(
    ProjectSelectionsSelectors.getActiveSelectionDocuments
  );
  activeQuoteAlternatives$: Observable<Alternative[]> = this.store.select(
    ProjectSelectionsSelectors.getActiveQuoteAlternatives
  );
  activeQuoteBaseAlternative$: Observable<Alternative | null> = this.store.select(
    ProjectSelectionsSelectors.getActiveSelectionBaseAlternative
  );
  activeQuoteUnits$: Observable<(Unit | CustomUnit)[]> = this.store.select(
    ProjectSelectionsSelectors.getActiveSelectionUnits
  );
  activeQuoteDeletedUnits$: Observable<(Unit | CustomUnit)[]> = this.store.select(
    ProjectSelectionsSelectors.getActiveQuoteDeletedUnits
  );

  getOrderAlternative$ = this.store.select(ProjectSelectionsSelectors.getOrderAlternative);

  activeQuoteId$ = this.store.select(ProjectSelectionsSelectors.getActiveSelectionId);

  offerPriceMultiplier$ = this.store
    .select(ProjectSelectionsSelectors.getActiveQuotePriceMultiplier)
    .pipe(distinctUntilChanged());
  offerStatistics$ = this.store
    .select(ProjectSelectionsSelectors.getActiveQuoteStatistics)
    .pipe(distinctUntilChanged());
  offerCustomCurrency$ = this.store
    .select(ProjectSelectionsSelectors.getActiveQuoteCustomCurrency)
    .pipe(distinctUntilChanged());
  offerCostsCustomCurrency$ = this.store
    .select(ProjectSelectionsSelectors.getActiveQuoteCostsCustomCurrency)
    .pipe(distinctUntilChanged());
  activeSelectionCurrencySymbol$ = this.store
    .select(ProjectSelectionsSelectors.getActiveSelectionCurrencySymbol)
    .pipe(distinctUntilChanged());
  activeSelectionCurrencySymbol = this.store.selectSignal(ProjectSelectionsSelectors.getActiveSelectionCurrencySymbol);
  offerCustomCurrencyMultiplier$ = this.store
    .select(ProjectSelectionsSelectors.getActiveQuoteCustomCurrencyMultiplier)
    .pipe(distinctUntilChanged());

  activeUnitId$: Observable<string> = this.store.select(ProjectSelectionsSelectors.getActiveUnitId);
  activeUnit$: Observable<Unit | undefined> = this.store.select(ProjectSelectionsSelectors.getActiveUnit);

  customUnitCreateSuccesfully$ = this.actions$.pipe(ofActionSuccessful(SelectionActions.CustomUnitCreateSuccess));
  customAccessoryCreateSuccesfully$ = this.actions$.pipe(
    ofActionSuccessful(SelectionActions.CustomAccessoryCreateSuccess)
  );
  assignPerformanceAccessoriesSuccess$ = this.actions$.pipe(ofActionSuccessful(SelectionActions.AssignAccessories));

  /* Computed properties */
  activeSelectionStatus = computed(() => this.activeSelectionSignal()?.details.status);

  constructor(spinnerService: SpinnerService, store: Store, actions$: Actions) {
    super(store, spinnerService, actions$);

    this.addSpinnerActions([
      ProjectActions.Load,
      SelectionActions.CreateUnitFromBleen,
      SelectionActions.CreateUnitAndConfigFromBleen,
      SelectionActions.CreateUnitFromTemplate,
      SelectionActions.AssignAccessories,
      SelectionActions.CustomUnitCreate,
      SelectionActions.ConfirmOrder,
      SelectionActions.AddExternalUnit,
      SelectionActions.Consolidate,
      SelectionActions.UpgradeModels,
      SelectionActions.SetStatisticData,
      SelectionActions.DownloadOrderPDF,
      SelectionActions.DownloadOfferPDF,
      SelectionActions.ExportOfferXls,
      SelectionActions.CreateAccessoryCustom,
      SelectionActions.DeleteAccessoryCustom,
    ]);
    this.addSpinnerActions(
      [
        SelectionActions.Duplicate,
        SelectionActions.Delete,
        SelectionActions.GoToUnitConfiguration,
        SelectionActions.DeleteAlternativeUnits,
      ],
      false
    );
  }

  get selections() {
    return this.store.selectSnapshot(ProjectSelectors.projectSelections);
  }

  get activeSelectionId() {
    return this.store.selectSnapshot(ProjectSelectionsSelectors.getActiveSelectionId);
  }

  get activeQuoteCode() {
    return this.store.selectSnapshot(ProjectSelectionsSelectors.getActiveSelectionCode);
  }

  get activeSelection() {
    return this.store.selectSnapshot(ProjectSelectionsSelectors.getActiveSelection);
  }

  getUnitFromActiveSelection(unitId: string): Observable<Unit | undefined> {
    return this.store.select(ProjectSelectionsSelectors.getActiveSelectionUnitById(unitId));
  }

  getUnitFromActiveSelectionAsSignal(unitId: string): Signal<Unit | undefined> {
    return this.store.selectSignal(ProjectSelectionsSelectors.getActiveSelectionUnitById(unitId));
  }

  activeUnitHasExternalUnit(unitId: string): Observable<boolean> {
    return this.getUnitFromActiveSelection(unitId).pipe(map(unit => !!unit?.childUnitId));
  }

  getAdditionalInfo(): Observable<AdditionalInfo[] | undefined> {
    return this.store.select(ProjectSelectionsSelectors.getAdditionalInfo);
  }

  get activeUnit() {
    return this.store.selectSnapshot(ProjectSelectionsSelectors.getActiveUnit);
  }

  get activeQuoteBaseAlternative() {
    return this.store.selectSnapshot(ProjectSelectionsSelectors.getActiveSelectionBaseAlternative);
  }

  get activeQuoteAllAlternatives() {
    return this.store.selectSnapshot(ProjectSelectionsSelectors.getActiveQuoteAllAlternatives);
  }

  get currencySymbol(): string {
    const selection = this.activeSelection;
    return CurrencyUtils.getCurrencySymbolFromCode(selection?.details?.currencyCode ?? selection?.currencyCode);
  }

  getUnit(unitId?: string): Unit | undefined {
    if (!unitId) {
      return;
    }
    return this.store.selectSnapshot(ProjectSelectionsSelectors.getActiveSelectionUnitById(unitId));
  }

  @Dispatch()
  createQuote(projectId: string, organizationId: number) {
    return new SelectionActions.Create(projectId, organizationId);
  }

  @Dispatch()
  duplicateQuote(selectionId: string) {
    return new SelectionActions.Duplicate(selectionId);
  }

  @Dispatch()
  addQuoteToProject(selection: Selection) {
    return new SelectionActions.Add(selection);
  }

  @Dispatch()
  deleteQuote(selectionId: string) {
    return new SelectionActions.Delete(selectionId);
  }

  @Dispatch()
  updateQuote(selectionId: string, title: string) {
    return new SelectionActions.Update(selectionId, title);
  }

  @Dispatch()
  updateQuoteCurrencyMultiplier(currencyMultiplier: number | null) {
    return new SelectionActions.UpdateCurrencyMultiplier(currencyMultiplier);
  }

  @Dispatch()
  updateGlobalPriceMultiplier() {
    return new SelectionActions.UpdateGlobalPriceMultiplier();
  }

  @Dispatch()
  updateQuotePriceMultiplier(priceMultiplier: number) {
    return new SelectionActions.UpdatePriceMultiplier(priceMultiplier);
  }

  @Dispatch()
  updateQuoteCustomCurrency(customCurrency: string | null) {
    return new SelectionActions.UpdateCustomCurrency(customCurrency);
  }

  @Dispatch()
  toggleQuoteUseCustomCurrency() {
    return new SelectionActions.ToggleUseCustomCurrency();
  }

  @Dispatch()
  toggleIsHot() {
    return new SelectionActions.ToggleIsHot();
  }

  @Dispatch()
  updateQuoteState(selectionData: Partial<SelectionDetails>) {
    return new SelectionActions.UpdateStateDetails(selectionData);
  }

  @Dispatch()
  consolidateQuote(projectId: string, selectionId: string) {
    return new SelectionActions.Consolidate(projectId, selectionId);
  }

  @Dispatch()
  draftQuote(projectId: string, selectionId: string) {
    return new SelectionActions.DraftQuote(projectId, selectionId);
  }

  @Dispatch()
  confirmAlternative(projectId: string, selectionId: string, alternativeId: string) {
    return new SelectionActions.ConfirmAlternative(projectId, selectionId, alternativeId);
  }

  @Dispatch()
  checkModelsHasUpgrade() {
    return new SelectionActions.CheckModelsHasUpgrade();
  }

  @Dispatch()
  refreshPrice() {
    return new SelectionActions.RefreshPrice();
  }

  @Dispatch()
  markUnitAlertsSeen(selectionId: string, unitId: string, severity: UnitAlertSeenSeverity) {
    return new SelectionActions.MarkUnitAlertsSeen(selectionId, unitId, severity);
  }

  @Dispatch()
  softDeleteUnit(selectionId: string, unitId: string) {
    return new SelectionActions.UnitSoftDelete(selectionId, unitId);
  }

  @Dispatch()
  restoreUnit(selectionId: string, unitId: string) {
    return new SelectionActions.UnitRestore(selectionId, unitId);
  }

  @Dispatch()
  deleteUnit(selectionId: string, unitId: string) {
    return new SelectionActions.UnitDelete(selectionId, unitId);
  }

  @Dispatch()
  createUnitFromTemplate(selectionId: string, alternativeId: string, entityId: string, entityType: string) {
    return new SelectionActions.CreateUnitFromTemplate(selectionId, alternativeId, entityId, entityType);
  }

  @Dispatch()
  createUnitFromBleen(selectionId: string, alternativeId: string, bleenId: string) {
    return new SelectionActions.CreateUnitFromBleen(selectionId, alternativeId, bleenId);
  }

  @Dispatch()
  createUnitAndConfigFromBleen(selectionId: string, alternativeId: string, bleenId: string) {
    return new SelectionActions.CreateUnitAndConfigFromBleen(selectionId, alternativeId, bleenId);
  }

  @Dispatch()
  duplicateUnit(selectionId: string, unitId: string) {
    return new SelectionActions.UnitDuplicate(selectionId, unitId);
  }

  @Dispatch()
  moveUnit(unitId: string, position: number) {
    return new SelectionActions.UnitMove(unitId, position);
  }

  @Dispatch()
  moveUnitCustom(unitId: string, position: number) {
    return new SelectionActions.MoveUnitCustom(unitId, position);
  }

  @Dispatch()
  updateQuantity(unitId: string, alternativeId: string, quantity: number) {
    return new SelectionActions.UpdateUnitQuantity(unitId, alternativeId, quantity);
  }

  @Dispatch()
  updateUnitCustomQuantity(unitId: string, alternativeId: string, quantity: number) {
    return new SelectionActions.UpdateUnitCustomQuantity(unitId, alternativeId, quantity);
  }

  @Dispatch()
  createAlternative(selectionId: string) {
    return new SelectionActions.AlternativeCreate(selectionId);
  }

  @Dispatch()
  deleteAlternative(selectionId: string, alternativeId: string) {
    return new SelectionActions.AlternativeDelete(selectionId, alternativeId);
  }

  @Dispatch()
  updateAlternativePriceForCustomer(alternativeId: string, priceForCustomer: number | null) {
    return new SelectionActions.AlternativeUpdatePriceForCustomer(alternativeId, priceForCustomer);
  }

  @Dispatch()
  togglePricelist(selectionId: string, unitId: string) {
    return new SelectionActions.AlternativePricelist(selectionId, unitId);
  }

  @Dispatch()
  linkUnitAlternative(selectionId: string, unitId: string, alternativeId: string, isCustom: boolean) {
    return isCustom
      ? new SelectionActions.UnitCustomAlternativeLink(selectionId, unitId, alternativeId)
      : new SelectionActions.UnitAlternativeLink(selectionId, unitId, alternativeId);
  }

  @Dispatch()
  unlinkUnitAlternative(selectionId: string, unitId: string, alternativeId: string, isCustom: boolean) {
    return isCustom
      ? new SelectionActions.UnitCustomAlternativeUnlink(selectionId, unitId, alternativeId)
      : new SelectionActions.UnitAlternativeUnlink(selectionId, unitId, alternativeId);
  }

  @Dispatch()
  moveAlternativeRow(alternativeId: string, moveChildRequest: MoveChildRequest) {
    return new SelectionActions.AlternativeMoveRow(alternativeId, moveChildRequest);
  }

  @Dispatch()
  createAlternativeExtra(alternativeId: string) {
    return new SelectionActions.AlternativeExtraCreate(alternativeId);
  }

  @Dispatch()
  deleteAlternativeExtra(alternativeExtraId: string) {
    return new SelectionActions.AlternativeExtraDelete(alternativeExtraId);
  }

  @Dispatch()
  updateAlternativeExtraPrice(alternativeExtraId: string, price: number) {
    return new SelectionActions.AlternativeExtraUpdatePrice(alternativeExtraId, price);
  }

  @Dispatch()
  updateAlternativeExtraCustomerPrice(alternativeExtraId: string, customer: number) {
    return new SelectionActions.AlternativeExtraUpdateCustomerPrice(alternativeExtraId, customer);
  }

  @Dispatch()
  updateAlternativeExtraName(alternativeExtraId: string, name: string) {
    return new SelectionActions.AlternativeExtraUpdateName(alternativeExtraId, name);
  }

  /** @deprecated Use {@link patchSelection} instead */
  @Dispatch()
  editNote(projectId: string, selectionId: string, note: string) {
    return new SelectionActions.EditNote(projectId, selectionId, note);
  }

  @Dispatch()
  patchSelection(patch: Partial<{ title: string; notes: string }>) {
    return new SelectionActions.PatchSelection(patch);
  }

  updateCurrencyCode = (currencyCode: string, requestTraceId?: string) => {
    this.store.dispatch(new SelectionActions.UpdateCurrencyCode(currencyCode, requestTraceId));
    return this.onActionErrored(SelectionActions.UpdateCurrencyCode);
  };

  @Dispatch()
  editAlternativeDescription(projectId: string, selectionId: string, alternativeId: string, note: string) {
    return new SelectionActions.EditAlternativeDescription(projectId, selectionId, alternativeId, note);
  }

  @Dispatch()
  shareOffer(
    email: string[],
    priceVisibility: string,
    priceType: string,
    infoVisibility: string,
    dataSheetVisibility: string,
    documentsVisibility: string
  ) {
    return new SelectionActions.ShareOffer(
      email,
      priceVisibility,
      priceType,
      infoVisibility,
      dataSheetVisibility,
      documentsVisibility
    );
  }

  @Dispatch()
  openShared(params: {
    priceVisibility: string;
    priceType: string;
    noteVisibility: string;
    datasheetVisibility: string;
    documentVisibility: string;
  }) {
    return new SelectionActions.OpenShared(params);
  }

  @Dispatch()
  exportOfferXls(fileName: string, priceType?: string) {
    return new SelectionActions.ExportOfferXls(fileName, priceType);
  }

  @Dispatch()
  updateOrderFields(selectionId: string, orderFields: UpdateOrderFieldsRequest) {
    return new SelectionActions.UpdateOrderFields(selectionId, orderFields);
  }

  @Dispatch()
  updateOfferRecipient(selectionId: string, offerRecipient: string) {
    return new SelectionActions.UpdateOfferRecipient(selectionId, offerRecipient);
  }

  @Dispatch()
  updateOfferRecipientAddress(selectionId: string, recipientAddress: string) {
    return new SelectionActions.UpdateAddress(selectionId, recipientAddress);
  }

  @Dispatch()
  addAdditionalInfo(
    selectionId: string,
    title: string,
    description: string,
    inEvidence: boolean,
    categoryId: string,
    templateId: string
  ) {
    return new SelectionActions.AddAdditionalInfo(selectionId, title, description, inEvidence, categoryId, templateId);
  }

  @Dispatch()
  updateAdditionalInfo(
    selectionId: string,
    infoId: string,
    title: string,
    description: string,
    inEvidence: boolean,
    categoryId: string,
    templateId: string
  ) {
    return new SelectionActions.UpdateAdditionalInfo(
      selectionId,
      infoId,
      title,
      description,
      inEvidence,
      categoryId,
      templateId
    );
  }

  @Dispatch()
  deleteAdditionalInfo(selectionId: string, infoId: string) {
    return new SelectionActions.DeleteAdditionalInfo(selectionId, infoId);
  }

  @Dispatch()
  finalizeVersion(selectionId: string) {
    return new SelectionActions.FinalizeVersion(selectionId);
  }

  @Dispatch()
  createRevision(selectionId: string) {
    return new SelectionActions.CreateRevision(selectionId);
  }

  @Dispatch()
  setStatisticData(selectionId: string, statisticData: StatisticDataArray[]) {
    return new SelectionActions.SetStatisticData(selectionId, statisticData);
  }

  @Dispatch()
  updatePriceValidityDate(selectionId: string, priceValidityDate: string | null) {
    return new SelectionActions.UpdatePriceValidityDate(selectionId, priceValidityDate);
  }

  @Dispatch()
  toggleTestPriceList(selectionId: string) {
    return new SelectionActions.ToggleTestPriceList(selectionId);
  }

  @Dispatch()
  updateExpiringDate(selectionId: string, expiringDate: string | null) {
    return new SelectionActions.UpdateExpiringDate(selectionId, expiringDate);
  }

  datasheetCreatePdf(unitId: string, requestTraceId?: string) {
    this.store.dispatch(new SelectionActions.DatasheetCreatePdf(unitId, requestTraceId));

    return this.onActionErrored(SelectionActions.DatasheetCreatePdf);
  }

  @Dispatch()
  datasheetDownloadPdf(fileName: string, customFileName?: string) {
    return new SelectionActions.DatasheetDownloadPdf(fileName, customFileName);
  }

  @Dispatch()
  cofirmOrder(idSelection: string) {
    return new SelectionActions.ConfirmOrder(idSelection);
  }

  @Dispatch()
  updateAccessoryQuantity = (accessoryId: string, quantity: number) =>
    new SelectionActions.UpdateAccessoryQuantity(accessoryId, quantity);

  @Dispatch()
  goToUnitConfiguration = (unitId: string) => new SelectionActions.GoToUnitConfiguration(unitId);

  @Dispatch()
  createCustomUnit = (unitCustom: CustomUnit) => new SelectionActions.CustomUnitCreate(unitCustom);

  @Dispatch()
  editCustomUnit = (unitId: string, unitCustom: CustomUnit) => new SelectionActions.CustomUnitEdit(unitId, unitCustom);

  @Dispatch()
  unitCustomSoftDelete(selectionId: string, unitId: string) {
    return new SelectionActions.UnitCustomSoftDelete(selectionId, unitId);
  }

  @Dispatch()
  unitCustomRestore(selectionId: string, unitId: string) {
    return new SelectionActions.UnitCustomRestore(selectionId, unitId);
  }

  @Dispatch()
  unitCustomDelete(selectionId: string, unitId: string) {
    return new SelectionActions.UnitCustomDelete(selectionId, unitId);
  }

  @Dispatch()
  createAccessoryCustom(unitId: string, accessoryCustom: CustomAccessory, isCustomUnit = false) {
    return new SelectionActions.CreateAccessoryCustom(unitId, accessoryCustom, isCustomUnit);
  }

  @Dispatch()
  updateAccessoryCustom(unitId: string, accessory: CustomAccessory, isCustomUnit = false) {
    return new SelectionActions.UpdateAccessoryCustom(unitId, accessory, isCustomUnit);
  }

  @Dispatch()
  deleteAccessoryCustom(unitId: string, accessoryId: string, isCustomUnit = false) {
    return new SelectionActions.DeleteAccessoryCustom(unitId, accessoryId, isCustomUnit);
  }

  @Dispatch()
  updateUnitWarranty = (unitId: string, warrantyCode: string, alternativeId: string) =>
    new SelectionActions.UpdateUnitWarranty(unitId, warrantyCode, alternativeId);

  @Dispatch()
  downloadWorkingConditionsRequestLog = () => new SelectionActions.DownloadWorkingConditionsRequestLog();

  @Dispatch()
  downloadPerformanceRequestLog = () => new SelectionActions.DownloadPerformanceRequestLog();

  @Dispatch()
  updateOfferProbability = probability => new SelectionActions.UpdateOfferProbability(probability);

  @Dispatch()
  updateAccessoryPrice = (accessoryId: string, price: number) =>
    new SelectionActions.UpdateAccessoryPrice(accessoryId, price);

  @Dispatch()
  assignAccessories = (answers: PerformanceAccessoryAnswer[]) => new SelectionActions.AssignAccessories(answers);

  @Dispatch()
  setOfferLost = () => new SelectionActions.SetOfferLost();

  @Dispatch()
  updateCustomUnitWarranty = (unitId: string, warrantyCode: string, alternativeId: string) =>
    new SelectionActions.UpdateCustomUnitWarranty(unitId, warrantyCode, alternativeId);

  @Dispatch()
  downloadOrderLog = (scope?: SelectionRequestLogScope) => new SelectionActions.DownloadOrderLog(scope);

  @Dispatch()
  downloadOfferPDF = (
    priceVisibility: string,
    priceType: string,
    infoVisibility: string,
    dataSheetVisibility: string,
    documentsVisibility: string
  ) =>
    new SelectionActions.DownloadOfferPDF(
      priceVisibility,
      priceType,
      infoVisibility,
      dataSheetVisibility,
      documentsVisibility
    );

  @Dispatch()
  downloadOrderPDF = (productionSiteCode: string, priceType?: string | PriceTypeEnum) =>
    new SelectionActions.DownloadOrderPDF(productionSiteCode, priceType);

  addExternalUnit = (bleenId: string, quantity: number, workingConditions: any, requestTraceId?: string) => {
    this.store.dispatch(new SelectionActions.AddExternalUnit(bleenId, quantity, workingConditions, requestTraceId));
    return this.onActionResult(SelectionActions.AddExternalUnit);
  };

  @Dispatch()
  upgradeModels = () => new SelectionActions.UpgradeModels();

  @Dispatch()
  updateUnitDescription = (unitId: string, description?: string) =>
    new SelectionActions.UpdateUnitDescription(unitId, description);

  @Dispatch()
  updateUnitCustomDescription = (unitCustomId: string, description?: string) =>
    new SelectionActions.UpdateUnitCustomDescription(unitCustomId, description);

  @Dispatch()
  deleteAlternativeUnits = (alternativeId: string, unitIds: string[]) =>
    new SelectionActions.DeleteAlternativeUnits(alternativeId, unitIds);
}
